import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { t } from 'i18next'
import { useMemo } from 'react'
import Moment from 'react-moment'
import { useNavigate, useParams } from 'react-router-dom'
import { styled } from 'styled-components'
import { Badge, ListPageLayout, SectionLoadingState } from '../../../components'
import {
  BadgeColors,
  NO_DATA_PLACEHOLDER,
  WorkspaceDocumentSetStatus,
  dateFormat,
  workspaceDocumentSetAllOwnersStatus,
  workspaceDocumentSetStatuses,
} from '../../../constants/constants'
import { useUsers, useWorkspaceDetails } from '../../../hooks'
import { ConformityChecksConfigDocumentSet } from '../../../store/reducers/workspaceReducer'
import { routePaths } from '../../RootPage'
import { workspacePanelTypes, workspaceTabs } from '../WorkspaceDetailsPage'

const SectionLoadingStateContainer = styled.div`
  display: flex;
  height: calc(100vh - 224px);
`

interface ExtendedConformityChecksConfigDocumentSet
  extends ConformityChecksConfigDocumentSet {
  key: string
  ownerName?: string
}

interface WorkspaceConformityDocumentsPanelProps {
  statusFilter: WorkspaceDocumentSetStatus
  ownerFilter: string
}

const WorkspaceConformityDocumentsPanel = ({
  statusFilter,
  ownerFilter,
}: WorkspaceConformityDocumentsPanelProps) => {
  const navigate = useNavigate()
  const { workspaceId } = useParams()
  const { selectedWorkspace } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })

  const { findUser } = useUsers({})

  const loading = false

  const formattedData: ExtendedConformityChecksConfigDocumentSet[] =
    useMemo(() => {
      const extendedDocuments =
        selectedWorkspace?.config?.conformityChecksConfig?.documents.map(
          (document, index) => ({
            ...document,
            key: `${document.id}-${index}`,
            ownerName: findUser(document.ownerId || '')?.displayName || '',
            documentSetStatus:
              document.documentSetStatus ?? workspaceDocumentSetStatuses.OPENED,
          })
        ) || []

      const result = extendedDocuments.filter((document) => {
        // match status and owner!
        const statusMatches =
          statusFilter === workspaceDocumentSetStatuses.ALL ||
          document.documentSetStatus === statusFilter

        const ownerMatches =
          ownerFilter === workspaceDocumentSetAllOwnersStatus ||
          document.ownerId === ownerFilter

        return statusMatches && ownerMatches
      })

      return result
    }, [selectedWorkspace, findUser, statusFilter, ownerFilter])

  const columns: ColumnsType<ExtendedConformityChecksConfigDocumentSet> = [
    {
      title: t('Title'),
      dataIndex: 'title',
      key: 'title',
      width: '50%',
      sorter: (
        a: ExtendedConformityChecksConfigDocumentSet,
        b: ExtendedConformityChecksConfigDocumentSet
      ) => (a.title && b.title ? a.title.localeCompare(b.title) : -1),
      render: (text) => text || NO_DATA_PLACEHOLDER,
    },
    {
      title: t('Owner'),
      dataIndex: 'ownerName',
      key: 'ownerName',
      className: 'max-with-deviation',
      sorter: (
        a: ExtendedConformityChecksConfigDocumentSet,
        b: ExtendedConformityChecksConfigDocumentSet
      ) =>
        a.ownerName && b.ownerName
          ? a.ownerName.localeCompare(b.ownerName)
          : -1,
      render: (text) => <>{text || NO_DATA_PLACEHOLDER}</>,
    },
    {
      title: t('Date'),
      dataIndex: 'creationDate',
      key: 'creationDate',
      className: 'max-with-deviation',
      sorter: (
        a: ExtendedConformityChecksConfigDocumentSet,
        b: ExtendedConformityChecksConfigDocumentSet
      ) =>
        a.creationDate && b.creationDate
          ? a.creationDate.localeCompare(b.creationDate)
          : -1,
      render: (textDate) => (
        <>
          {textDate ? (
            <Moment local format={dateFormat.PRIMARY}>
              {textDate}
            </Moment>
          ) : (
            NO_DATA_PLACEHOLDER
          )}
        </>
      ),
    },
    {
      title: t('Status'),
      dataIndex: 'documentSetStatus',
      key: 'documentSetStatus',
      sorter: (
        a: ExtendedConformityChecksConfigDocumentSet,
        b: ExtendedConformityChecksConfigDocumentSet
      ) =>
        a.documentSetStatus && b.documentSetStatus
          ? a.documentSetStatus.localeCompare(b.documentSetStatus)
          : -1,
      render: (text) => (
        <>
          <Badge
            color={
              text === workspaceDocumentSetStatuses.CLOSED
                ? BadgeColors.STATUS_CLOSED
                : BadgeColors.STATUS_OPENED
            }
          >
            {t(
              `workspaceDetailsPage.documentSetStatus.${
                text || workspaceDocumentSetStatuses.OPENED
              }`
            )}
          </Badge>
        </>
      ),
    },
  ]

  const handleClick = (record: ExtendedConformityChecksConfigDocumentSet) => {
    if (record.id) {
      navigate(
        `${routePaths.WORKSPACE_DETAILS}/${workspaceId}/${workspacePanelTypes.DOCUMENTS}/${workspaceTabs.INFO}/${record.id}`
      )
    } else {
      console.error('The clicked document has no id.')
    }
  }

  return loading ? (
    <SectionLoadingStateContainer>
      <SectionLoadingState />
    </SectionLoadingStateContainer>
  ) : (
    <ListPageLayout>
      <Table
        data-testid="data-sources-list-table"
        rowClassName="page-list-table-row clickable"
        columns={columns}
        dataSource={formattedData}
        pagination={false}
        scroll={{ y: 'calc(100vh - 170px)' }}
        onRow={(record) => ({
          onClick: () => handleClick(record),
        })}
      />
    </ListPageLayout>
  )
}

export default WorkspaceConformityDocumentsPanel
